import { useEffect, useRef, useState } from "react";
import { CloudUpload, FolderOpen, RefreshCw, X, RotateCcw } from "lucide-react";
import {
  hasGoogleDriveConfiguration,
  pickDriveImages,
} from "./google-drive-picker";
import {
  isStorageAccessFailure,
  mapBulkContracts,
  photoMimeType,
  reconcileBulkEntries,
  uploadSignedContract,
  uploadWithConcurrency,
  validateBulkFiles,
  withAdmissionRetry,
} from "./media-workflow";
import "./bulk-media.css";

const createId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** A reusable photographer/admin bulk uploader; all mutations go through existing event-scoped APIs. */
export default function BulkMediaUploader({ eventId, api, onUploaded }) {
  const [entries, setEntries] = useState([]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [driveJob, setDriveJob] = useState(null);
  const [driveProgress, setDriveProgress] = useState(null);
  const [bulkJob, setBulkJob] = useState(null);
  const controllers = useRef(new Map());
  const eventRef = useRef(eventId);
  const apiRef = useRef(api);
  const onUploadedRef = useRef(onUploaded);
  useEffect(() => {
    apiRef.current = api;
  }, [api]);
  useEffect(() => {
    onUploadedRef.current = onUploaded;
  }, [onUploaded]);
  useEffect(() => {
    eventRef.current = eventId;
    controllers.current.forEach((controller) => controller.abort());
    controllers.current.clear();
    setEntries([]);
    setDriveJob(null);
    setDriveProgress(null);
    setBulkJob(null);
    setWorking(false);
    setError("");
  }, [eventId]);
  useEffect(
    () => () => controllers.current.forEach((controller) => controller.abort()),
    [],
  );
  const bulkJobId = bulkJob?.id;
  const bulkJobPending = Number(bulkJob?.summary?.pending || 0) > 0;
  useEffect(() => {
    if (!eventId || !bulkJobId || !bulkJobPending) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const result = await apiRef.current.jobStatus(
          eventId,
          bulkJobId,
          "bulk",
        );
        if (!cancelled) {
          setBulkJob(result);
          setEntries((current) => reconcileBulkEntries(current, result));
          onUploadedRef.current?.();
        }
      } catch {
        // A manual refresh remains available if the status request is interrupted.
      }
    };
    const interval = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [bulkJobId, bulkJobPending, eventId]);

  function update(id, patch) {
    setEntries((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  }
  function selectFiles(files) {
    try {
      const accepted = validateBulkFiles(files);
      setEntries(
        accepted.map((file) => ({
          id: createId(),
          file,
          name: file.name,
          status: "queued",
          progress: 0,
          error: "",
        })),
      );
      setBulkJob(null);
      setError("");
    } catch (issue) {
      setError(issue.message);
    }
  }
  async function transfer(entry, contract) {
    const controller = new AbortController();
    controllers.current.set(entry.id, controller);
    update(entry.id, { status: "uploading", progress: 0, error: "" });
    try {
      await uploadSignedContract({
        eventId,
        file: entry.file,
        contract,
        api,
        signal: controller.signal,
        onProgress: (loaded, total) =>
          update(entry.id, {
            progress: Math.round((loaded / Math.max(total, 1)) * 100),
          }),
      });
      update(entry.id, { status: "processing", progress: 100 });
      return { ok: true };
    } catch (issue) {
      update(entry.id, {
        status: issue.name === "AbortError" ? "cancelled" : "failed",
        progress: 0,
        error: issue.message,
      });
      return { ok: false, error: issue };
    } finally {
      controllers.current.delete(entry.id);
    }
  }
  async function startBulk() {
    const pending = entries.filter((entry) =>
      ["queued", "paused"].includes(entry.status),
    );
    if (!eventId || !pending.length || working) return;
    setWorking(true);
    setError("");
    let stopped = false;
    let storageFailures = 0;
    let offset = 0;
    try {
      // Confirm the first signed PUT succeeds before creating 25+ upload records.
      // Misconfigured R2 CORS would otherwise produce hundreds of failed requests.
      while (
        offset < pending.length &&
        !stopped &&
        eventRef.current === eventId
      ) {
        const batchSize = offset === 0 ? 1 : 25;
        const batch = pending.slice(offset, offset + batchSize);
        batch.forEach((entry) =>
          update(entry.id, { status: "preparing", error: "" }),
        );
        let response;
        try {
          response = await api.createBulk(
            eventId,
            batch.map(({ file, id }) => ({
              kind: "professional",
              originalFilename: file.name,
              mimeType: photoMimeType(file),
              sizeBytes: file.size,
              clientUploadId: id,
              mode: "auto",
            })),
            createId(),
          );
        } catch (issue) {
          batch.forEach((entry) =>
            update(entry.id, { status: "failed", error: issue.message }),
          );
          setError(
            `Unable to prepare uploads: ${issue.message}. Remaining photos have been kept for retry.`,
          );
          stopped = true;
          break;
        }
        let mapped;
        try {
          mapped = mapBulkContracts(
            batch.map((entry) => entry.file),
            response,
          );
        } catch (issue) {
          batch.forEach((entry) =>
            update(entry.id, { status: "failed", error: issue.message }),
          );
          setError(issue.message);
          stopped = true;
          break;
        }
        setBulkJob(response.session);
        const { scheduled } = await uploadWithConcurrency(
          mapped,
          async (item, index) => {
            const entry = batch[index];
            if (item.error || !item.contract) {
              update(entry.id, {
                status: "failed",
                error: item.error || "Upload signing failed",
              });
              return;
            }
            update(entry.id, { photoId: item.photoId });
            const outcome = await transfer(entry, item.contract);
            if (!outcome.ok && isStorageAccessFailure(outcome.error)) {
              storageFailures += 1;
              if (offset === 0 || storageFailures >= 2) stopped = true;
            }
          },
          3,
          { shouldStop: () => stopped || eventRef.current !== eventId },
        );
        // Preserve files we did not attempt so they can resume without reselecting.
        for (const entry of batch.slice(scheduled)) {
          update(entry.id, {
            status: "paused",
            error: "Upload paused; not transferred to storage.",
          });
        }
        offset += batch.length;
        onUploaded?.();
      }
      if (stopped) {
        for (const entry of pending.slice(offset)) {
          update(entry.id, {
            status: "paused",
            error: "Upload paused; not transferred to storage.",
          });
        }
        if (storageFailures)
          setError(
            "Storage uploads are blocked. Check the Cloudflare R2 bucket CORS rule for this website, PUT, Content-Type and ETag; then retry. Other selected photos remain queued.",
          );
      }
    } catch (issue) {
      setError(issue.message);
    } finally {
      setWorking(false);
    }
  }
  async function initiateEntry(entry) {
    let clientUploadId =
      entry.status === "cancelled"
        ? createId()
        : entry.clientUploadId || entry.id;
    update(entry.id, { clientUploadId, error: "" });
    const body = () => ({
      kind: "professional",
      originalFilename: entry.file.name,
      mimeType: photoMimeType(entry.file),
      sizeBytes: entry.file.size,
      clientUploadId,
      mode: "auto",
    });
    try {
      return await withAdmissionRetry(() => api.initiate(eventId, body()));
    } catch (issue) {
      if (issue.code !== "UPLOAD_REPLAY_UNAVAILABLE") throw issue;
      // The old upload expired. A fresh ID is required only in that case;
      // the backend's stale-upload cleanup handles the abandoned record.
      clientUploadId = createId();
      update(entry.id, { clientUploadId });
      return withAdmissionRetry(() => api.initiate(eventId, body()));
    }
  }
  async function resumeEntry(entry) {
    try {
      const contract = await initiateEntry(entry);
      return await transfer(entry, contract);
    } catch (issue) {
      update(entry.id, { status: "failed", progress: 0, error: issue.message });
      return { ok: false, error: issue };
    }
  }
  async function retry(entry) {
    if (!eventId || working) return;
    setWorking(true);
    setError("");
    try {
      await resumeEntry(entry);
      onUploaded?.();
    } finally {
      setWorking(false);
    }
  }
  async function retryFailed() {
    const failed = entries.filter((entry) =>
      ["failed", "cancelled"].includes(entry.status),
    );
    if (!eventId || working || !failed.length) return;
    setWorking(true);
    setError("");
    let storageFailures = 0;
    try {
      // Event admission is a single lease: initiate serially, then PUT in parallel.
      const prepared = [];
      for (const entry of failed) {
        if (storageFailures >= 2 || eventRef.current !== eventId) break;
        try {
          prepared.push({ entry, contract: await initiateEntry(entry) });
        } catch (issue) {
          update(entry.id, {
            status: "failed",
            progress: 0,
            error: issue.message,
          });
        }
      }
      await uploadWithConcurrency(
        prepared,
        async ({ entry, contract }) => {
          const outcome = await transfer(entry, contract);
          if (!outcome.ok && isStorageAccessFailure(outcome.error))
            storageFailures++;
        },
        3,
        {
          shouldStop: () =>
            storageFailures >= 2 || eventRef.current !== eventId,
        },
      );
      if (storageFailures >= 2)
        setError(
          "Storage requests are still blocked or interrupted. Check R2 bucket CORS and connectivity before retrying again.",
        );
      onUploaded?.();
    } finally {
      setWorking(false);
    }
  }
  async function refreshJob(job, type) {
    try {
      const result = await api.jobStatus(eventId, job.id, type);
      if (type === "drive") setDriveJob(result);
      else {
        setBulkJob(result);
        setEntries((current) => reconcileBulkEntries(current, result));
      }
      onUploaded?.();
    } catch (issue) {
      setError(issue.message);
    }
  }
  async function importDrive() {
    if (!eventId || working) return;
    setWorking(true);
    setError("");
    setDriveProgress(null);
    try {
      const selection = await pickDriveImages();
      if (!selection) return;
      const jobs = [];
      // Drive ingestion happens in the backend request (the token is never persisted).
      // Keep each request small to limit gateway timeouts and show partial results.
      for (let offset = 0; offset < selection.fileIds.length; offset += 10) {
        if (eventRef.current !== eventId) break;
        const ids = selection.fileIds.slice(offset, offset + 10);
        try {
          const job = await api.importDrive(eventId, {
            accessToken: selection.accessToken,
            fileIds: ids,
            idempotencyKey: createId(),
          });
          jobs.push(job);
          setDriveJob(job);
        } catch (issue) {
          setError(
            `Drive batch ${Math.floor(offset / 10) + 1} failed: ${issue.message}`,
          );
        }
        setDriveProgress({
          completed: Math.min(offset + ids.length, selection.fileIds.length),
          total: selection.fileIds.length,
        });
        onUploaded?.();
      }
      if (!jobs.length && selection.fileIds.length)
        setError("No Drive batches were successfully submitted.");
    } catch (issue) {
      setError(issue.message);
    } finally {
      setWorking(false);
    }
  }
  const statusLine = (job) =>
    job &&
    `${job.summary?.ready || 0} ready · ${job.summary?.pending || 0} pending · ${job.summary?.failed || 0} failed`;
  const remainingCount = entries.filter((entry) =>
    ["queued", "paused"].includes(entry.status),
  ).length;
  const failureCount = entries.filter(
    (entry) => entry.status === "failed",
  ).length;
  const completedCount = entries.filter((entry) =>
    ["processing", "ready"].includes(entry.status),
  ).length;
  return (
    <section className="px-bulk" aria-label="Event media upload management">
      <div className="px-bulk-head">
        <div>
          <h3>Professional photo uploads</h3>
          <p>
            Up to 500 photographs per batch · 3 parallel transfers · large-file
            multipart upload
          </p>
        </div>
      </div>
      <label
        className={`px-bulk-drop ${!eventId || working ? "disabled" : ""}`}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          if (eventId && !working) selectFiles(event.dataTransfer.files);
        }}
      >
        <CloudUpload size={26} />
        <strong>Choose or drop photos for this event</strong>
        <small>JPEG, PNG, HEIC or HEIF</small>
        <input
          aria-label="Select multiple event photographs"
          type="file"
          accept="image/jpeg,image/png,image/heic,image/heif"
          multiple
          disabled={!eventId || working}
          onChange={(event) => {
            selectFiles(event.target.files);
            event.target.value = "";
          }}
        />
      </label>
      <div className="px-bulk-actions">
        <button
          type="button"
          className="button button-primary"
          disabled={!eventId || !remainingCount || working}
          onClick={startBulk}
        >
          <CloudUpload size={16} />{" "}
          {working ? "Working…" : `Upload ${remainingCount} selected`}
        </button>
        {failureCount > 0 && (
          <button
            type="button"
            className="button button-light"
            disabled={!eventId || working}
            onClick={retryFailed}
          >
            <RotateCcw size={16} /> Retry {failureCount} failed
          </button>
        )}
        <button
          type="button"
          className="button button-light"
          disabled={!eventId || working || !hasGoogleDriveConfiguration()}
          onClick={importDrive}
        >
          <FolderOpen size={16} /> Import from Google Drive
        </button>
      </div>
      {!hasGoogleDriveConfiguration() && (
        <small className="px-bulk-note">
          Google Drive Picker needs public Google OAuth Client ID and browser
          API key configuration.
        </small>
      )}
      {error && (
        <p className="px-bulk-error" role="alert">
          {error}
        </p>
      )}
      {entries.length > 0 && (
        <p className="px-bulk-summary" role="status">
          {completedCount} transferred · {failureCount} failed ·{" "}
          {remainingCount} queued/paused · {entries.length} selected
        </p>
      )}
      {entries.length > 0 && (
        <div className="px-bulk-list" aria-live="polite">
          {entries.map((entry) => (
            <div className="px-bulk-item" key={entry.id}>
              <div>
                <strong title={entry.name}>{entry.name}</strong>
                <small>{entry.error || entry.status}</small>
              </div>
              <progress
                max="100"
                value={entry.progress}
                aria-label={`${entry.name} upload progress`}
              />
              {["uploading"].includes(entry.status) ? (
                <button
                  type="button"
                  aria-label={`Cancel ${entry.name}`}
                  onClick={() => controllers.current.get(entry.id)?.abort()}
                >
                  <X size={16} />
                </button>
              ) : ["failed", "cancelled", "paused"].includes(entry.status) ? (
                <button
                  type="button"
                  aria-label={`Retry ${entry.name}`}
                  disabled={working}
                  onClick={() => retry(entry)}
                >
                  <RotateCcw size={16} />
                </button>
              ) : (
                <span>{entry.progress}%</span>
              )}
            </div>
          ))}
        </div>
      )}
      {bulkJob && (
        <div className="px-bulk-job">
          <span>Latest bulk session · {statusLine(bulkJob)}</span>
          <button type="button" onClick={() => refreshJob(bulkJob, "bulk")}>
            <RefreshCw size={15} /> Refresh status
          </button>
        </div>
      )}
      {driveProgress && (
        <p className="px-bulk-note" role="status">
          Drive batches: {driveProgress.completed}/{driveProgress.total} files
          submitted
        </p>
      )}
      {driveJob && (
        <div className="px-bulk-job">
          <span>Drive import · {statusLine(driveJob)}</span>
          <button type="button" onClick={() => refreshJob(driveJob, "drive")}>
            <RefreshCw size={15} /> Refresh status
          </button>
          <ul>
            {(driveJob.items || [])
              .filter((item) => item.error)
              .slice(0, 12)
              .map((item, i) => (
                <li key={item.fileId || i}>
                  {item.fileId}: {item.error.message}
                </li>
              ))}
          </ul>
        </div>
      )}
    </section>
  );
}
